import { useMemo } from "react";
import { FormattedMessage } from "react-intl";

import { JobRead } from "@nens/rana/api-v1";

import { useFileDirectoryQuery } from "@/queries/query-file";
import { useFileHistoryQuery } from "@/queries/query-file-history";

import { ResultFileError, ResultFileItem, ResultFileLoading } from "./result-file-item";

interface ResultDirectoryProps {
  job: JobRead;
  directoryId: string;
  expectedAmount: number;
  reduced: boolean;
}

export function ResultDirectory({
  directoryId,
  reduced,
  job,
  expectedAmount,
}: ResultDirectoryProps) {
  // Query directory history to find the closest item based on job.finishedAt
  const directoryHistoryQuery = useFileHistoryQuery(directoryId);

  // Directory ref is always "main" by default. Which means if you add any files they will be shown in the result and if you delete it you will not see anything.
  // So for now we get the history item with the closest date to the finishedAt date of the job. This is not perfect but it is better than nothing.
  // FUTURE: Get rid of this logic when https://github.com/nens/rana/issues/4854 is implemented
  const directoryRef = useMemo(() => {
    const historyItems = directoryHistoryQuery?.data?.items;
    if (!job.finishedAt) {
      throw new Error("Job finished at is undefined while looking at results");
    }
    if (!historyItems || historyItems.length === 0) {
      return undefined;
    }

    const finishedAt = new Date(job.finishedAt);
    const closestHistoryItem = historyItems.reduce((closest, current) => {
      const closestDiff = Math.abs(new Date(closest.createdAt).getTime() - finishedAt.getTime());
      const currentDiff = Math.abs(new Date(current.createdAt).getTime() - finishedAt.getTime());
      return currentDiff < closestDiff ? current : closest;
    });

    return closestHistoryItem.ref;
  }, [directoryHistoryQuery.data, job.finishedAt]);

  const directoryQuery = useFileDirectoryQuery(directoryId, directoryRef);

  if (directoryHistoryQuery.isPending || directoryQuery.isPending) {
    // Show skeleton loaders for expected files
    return Array.from({ length: expectedAmount || 1 }).map((_, idx) => (
      <ResultFileLoading key={`loading-${idx}`} reduced={reduced} />
    ));
  }

  const directoryItems = directoryQuery.data?.items;
  if (!directoryItems || directoryItems.length === 0) {
    return (
      <ResultFileError reduced={reduced}>
        <FormattedMessage
          id="processresults.directory_empty"
          defaultMessage="Could not fetch directory `{directoryId}` with ref {directoryRef} or its empty"
          values={{ directoryId, directoryRef }}
        />
      </ResultFileError>
    );
  }

  // Render only files from the directory
  return directoryItems.map((item) => {
    if (item.type !== "file") {
      return (
        <ResultFileError key={item.id} reduced={reduced}>
          <FormattedMessage
            id="processresults.file_is_directory"
            defaultMessage="Expected a file but found a directory `{directoryId}`"
            values={{ directoryId }}
          />
        </ResultFileError>
      );
    }

    return (
      <ResultFileItem key={item.id} fileId={item.id} fileRef={directoryRef} reduced={reduced} />
    );
  });
}
